/**
 * books 域 Zod 共享契约 — ADM-BOK-S001~S014
 *
 * 字段映射详见 dev-docs `03-数据字典/20-内容.md`
 */

import { z } from 'zod';
import { EditionLabelSchema } from './novel-edition-label';
import { NovelLengthClassSchema, NovelPriceLevelSchema } from './novel-length-price';
import { type NovelVisibility, NovelVisibilitySchema } from './novel-visibility';

const SlugSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/i, 'Slug must be alphanumeric / hyphens');

const LocaleSchema = z
  .string()
  .min(2)
  .max(16)
  .regex(/^[a-zA-Z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Must be BCP 47 locale');

/**
 * 章节定时发布输入 = **本地墙上时刻**（dev-docs `03-数据字典/20-内容.md` §3.2 / ADM-BOK-S009）：
 * `YYYY-MM-DDTHH:mm:ss`，**故意不带 offset / 时区**——其绝对性由服务端按书籍语言时区补全归一
 * （见 server `lib/scheduled-publish-normalize.ts`）。**禁用** `z.string().datetime()`（那要求带 offset，
 * 与墙上时刻语义矛盾；§3.2 全局「缺时区→VALIDATION_FAILED」规则对本字段的唯一 carve-out）。
 */

/** 裸墙上时刻 wire 格式（**仅形状**）；日历合法性由 `isValidWallClock` 二次校验 */
export const WALL_CLOCK_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/**
 * 墙上时刻**日历合法性**校验（codex #17 adversarial A）：形状正则只防形状，`Date.parse` 对非法日历
 * 日期会 silent rollover（`2026-02-31`→3 月 3 日 / `24:00:00`→次日）→ 静默存错日期。本函数组件解析 +
 * 范围校验 + `Date.UTC` round-trip 兜住 rollover，非法即 `false`（→ `VALIDATION_FAILED`，不静默归一）。
 * 单一真理源：server `lib/wall-clock-timezone.ts` 与 `WallClockTimeSchema` 都复用本函数，不重复正则。
 */
export function isValidWallClock(s: string): boolean {
  if (!WALL_CLOCK_RE.test(s)) return false;
  const y = +s.slice(0, 4);
  const mo = +s.slice(5, 7);
  const d = +s.slice(8, 10);
  const h = +s.slice(11, 13);
  const mi = +s.slice(14, 16);
  const se = +s.slice(17, 19);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || se > 59) return false;
  // Date.UTC 对非法日历日期会 rollover（Feb 31→Mar 3）；round-trip 组件全等才算合法
  const dt = new Date(Date.UTC(y, mo - 1, d, h, mi, se));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === mo - 1 &&
    dt.getUTCDate() === d &&
    dt.getUTCHours() === h &&
    dt.getUTCMinutes() === mi &&
    dt.getUTCSeconds() === se
  );
}

export const WallClockTimeSchema = z
  .string()
  .regex(
    WALL_CLOCK_RE,
    'scheduled_publish_at must be wall-clock YYYY-MM-DDTHH:mm:ss (no timezone offset)',
  )
  .refine(isValidWallClock, 'scheduled_publish_at must be a valid calendar date-time');

/** 多语言文案 jsonb shape：{ "zh": "...", "en": "...", ... }；zh/en 必填 */
export const I18nFieldsSchema = z
  .record(z.string().min(1).max(16), z.string().min(1).max(255))
  .refine((m) => 'zh' in m && 'en' in m, { message: 'zh and en are required' });
export type I18nFields = z.infer<typeof I18nFieldsSchema>;

/** 分类（ADM-BOK-S001） */
/**
 * 分类的创建 / 编辑共享字段。**不挂 `.default()`** —— Zod 4 的 `.partial()` 不抵消 `.default()`，
 * 挂在这里会让 PATCH 把没传的 `display_order` 一并产出成 `0`，而写侧判据是
 * `input.display_order !== undefined` ⇒ 改个分类名就把它排到最前面。缺省只施加在创建侧。
 */
const CategoryRequestFieldsSchema = z.object({
  slug: SlugSchema,
  name_i18n: I18nFieldsSchema,
  display_order: z.number().int().nonnegative(),
});
export const CreateCategoryRequestSchema = CategoryRequestFieldsSchema.extend({
  display_order: z.number().int().nonnegative().default(0),
});
export const UpdateCategoryRequestSchema = CategoryRequestFieldsSchema.partial()
  .omit({ slug: true })
  .extend({
    /**
     * 乐观并发前置条件（A6 §14.2 / §14.4，形态同 §16.2）：取载入时响应的 `updated_at` **逐字**回传
     * （保留亚秒精度）。服务端在行锁之内、任何写之前与目标行当前 `updated_at` 逐字比对，不等则整笔
     * 拒绝 `RESOURCE_CONFLICT` / `details.reason='STALE_SNAPSHOT'`，一个字段都不落。
     *
     * `name_i18n` 是**整份多语言 map、按全量快照提交** —— 没有这道闸时，两个运营各开一个语言 Tab
     * 编辑，后保存的那份会把先保存的那门语言整个覆盖掉，而双方都看不出异常。
     *
     * **省略 = 不做该检查**（脚本 / 数据修正类调用方）；交互式编辑面必须携带。
     */
    expected_updated_at: z.string().datetime().optional(),
  })
  .strict()
  .refine(
    // `expected_updated_at` 是前置条件不是可写字段：只带它 = 一次空更新，须与完全不传字段同样被拒
    // （A6 §14.2 明写这一条 → VALIDATION_FAILED）。
    (value) => Object.keys(value).filter((key) => key !== 'expected_updated_at').length > 0,
    { message: 'At least one mutable field is required' },
  );
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;
export type UpdateCategoryRequest = z.infer<typeof UpdateCategoryRequestSchema>;
export type CategoryResponse = {
  id: string;
  slug: string;
  name_i18n: Record<string, string>;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /**
   * 关联书籍数（接真）= 该分类下**书组**去重计数（`COUNT(DISTINCT novels.group_id)`，
   * 按书组而非物理行，避免 per-locale 译版重复计；仅计未软删 novels）。server LEFT JOIN novels 单查询出，无 N+1。
   */
  book_count: number;
};

/** 标签（ADM-BOK-S002） */
export const CreateTagRequestSchema = z.object({
  slug: SlugSchema,
  name_i18n: I18nFieldsSchema,
  category_hint: z.string().uuid().nullable().optional(),
});
export const UpdateTagRequestSchema = CreateTagRequestSchema.partial()
  .omit({ slug: true })
  .extend({
    /**
     * 乐观并发前置条件（A6 §14.2 / §14.4，形态同 §16.2）：取载入时响应的 `updated_at` **逐字**回传
     * （保留亚秒精度）。服务端在行锁之内、任何写之前与目标行当前 `updated_at` 逐字比对，不等则整笔
     * 拒绝 `RESOURCE_CONFLICT` / `details.reason='STALE_SNAPSHOT'`，一个字段都不落。
     *
     * `name_i18n` 是**整份多语言 map、按全量快照提交** —— 没有这道闸时，两个运营各开一个语言 Tab
     * 编辑，后保存的那份会把先保存的那门语言整个覆盖掉，而双方都看不出异常。
     *
     * **省略 = 不做该检查**（脚本 / 数据修正类调用方）；交互式编辑面必须携带。
     */
    expected_updated_at: z.string().datetime().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).filter((key) => key !== 'expected_updated_at').length > 0, {
    message: 'At least one mutable field is required',
  });
export type CreateTagRequest = z.infer<typeof CreateTagRequestSchema>;
export type UpdateTagRequest = z.infer<typeof UpdateTagRequestSchema>;
export type TagResponse = {
  id: string;
  slug: string;
  name_i18n: Record<string, string>;
  category_hint: string | null;
  is_active: boolean;
  /** A6 §14.4 `expected_updated_at` 的令牌来源（保留亚秒精度、逐字回传）*/
  updated_at: string;
  /**
   * 关联书籍数（接真）= 含该标签的**书组**去重计数（`tag.id = ANY(novels.tags)` +
   * `COUNT(DISTINCT novels.group_id)`，按书组、仅未软删）。server LEFT JOIN novels 单查询出。
   */
  book_count: number;
};

/**
 * 书籍（ADM-BOK-S003 / S004 / S005 / S006）— per-locale 建模（dev-docs PR #62/#76 ADR-005）
 *
 * 一本书 = 一个「书组」（`novel_groups` 一行，dd#970）。每个语言版本是 novels 表的独立一行，
 * 经 `novels.group_id` 挂书组；`source_novel_id` 退回纯译制语义（`A` 译自 `B`），**不表达分组**。
 * - 列表：按书组聚合，一组一条（NovelGroupSummary）；
 * - 详情：`GET /book-groups/:id` 返回书组级字段 + 该书组所有 per-locale 行
 *   （NovelGroupDetail.languages: NovelResponse[]）。
 * - 书组级字段（`cn_title` / 原作信息 / `source` / 展示基数）只经 `PATCH /book-groups/:id`（§16.4）改；
 *   `PATCH /books/:id`（§16.2）只写 per-locale 行级字段，提交书组级字段一律 VALIDATION_FAILED。
 */
const NovelSourceSchema = z.enum(['ORIGINAL', 'LICENSED', 'IMPORTED']);
/** 外部ID写入/清空必须同请求携带提供方；非空ID不能搭配NULL提供方。 */
function hasValidSourceProviderDependency(value: {
  source_provider?: string | null;
  source_external_id?: string | null;
}): boolean {
  return (
    (value.source_external_id === undefined || value.source_provider !== undefined) &&
    (value.source_external_id == null || value.source_provider != null)
  );
}

const SourceProviderSchema = z.enum(['TENTOO']);
const AgeRatingSchema = z.enum(['ALL_AGES', 'TEEN_13', 'MATURE_16', 'ADULT_18']);
const SerialStatusSchema = z.enum(['ONGOING', 'COMPLETED', 'HIATUS']);
/**
 * 运营推荐语多语言池（novels.recommendation_text_i18n，ADM-BOK / ADM-OPS-S009 决策 B）：locale → 推荐语。
 * **可选 + 部分配置**（书可暂未配 / 只配部分语言）；书架推荐不要求推荐语（A2 §8.5），清空照常保存。
 * superRefine 对齐 DB CHECK（novels_recommendation_text_i18n_valid：`::text` ≤8192）——bot/adversarial：
 * DB 校的是 PostgreSQL `jsonb::text`（键值间含空格 `": "` / `, `），compact JSON.stringify 会**低估**；
 * 故按 jsonb 形态估算（compact + 每键约 2 空格：冒号后 + 逗号后）+ 保守 8000 < 8192，提前 VALIDATION_FAILED(422)
 * 而非过 Zod 后撞 DB CHECK 抛未捕获 500。键数另设上限兜底（系统语言闭集，64 远超实际）。
 */
const RECOMMENDATION_TEXT_I18N_MAX_BYTES = 8000;
const RECOMMENDATION_TEXT_I18N_MAX_LOCALES = 64;
const RecommendationTextI18nSchema = z
  .record(LocaleSchema, z.string().min(1).max(2000))
  .superRefine((val, ctx) => {
    const keys = Object.keys(val);
    if (keys.length > RECOMMENDATION_TEXT_I18N_MAX_LOCALES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `recommendation_text_i18n exceeds ${RECOMMENDATION_TEXT_I18N_MAX_LOCALES} locales`,
      });
    }
    // jsonb::text 比 compact 多每键约 2 空格（": " + ", "）；保守估算上界
    const jsonbApproxLen = JSON.stringify(val).length + 2 * keys.length;
    if (jsonbApproxLen > RECOMMENDATION_TEXT_I18N_MAX_BYTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `recommendation_text_i18n serialized size (~jsonb) exceeds ${RECOMMENDATION_TEXT_I18N_MAX_BYTES} bytes`,
      });
    }
  })
  .optional();

/**
 * 封面 URL：**相对 ref（/开头）或绝对 http(s) URL**。
 * 相对 ref（运营经 POST /books/cover-upload 上传后拿到，形如 `/api/v1/static/covers/{id}/cover.png`）→
 * 由 api-server serve 时按 per-app origin 拼成绝对 URL（dev-docs 03-数据字典/20-内容.md §1.4 resolveCoverUrl）；
 * 绝对 URL（外部 vendor / 历史 / 占位）→ 原样透传。故**不能**再用 `.url()`（它拒相对 ref）。
 */
export const CoverUrlSchema = z
  .string()
  .min(1)
  .max(1024)
  // 相对 ref 必须单 `/` 开头（拒协议相对 `//host` —— 它 startsWith('/') 但会被解析成外部 host，
  // serve 时按 origin 拼接有外链/重定向风险）；或绝对 http(s) URL。
  .refine((v) => (v.startsWith('/') && !v.startsWith('//')) || /^https?:\/\//.test(v), {
    message: 'cover_url must be a single-slash relative ref (/path) or an absolute http(s) URL',
  });

/** POST /api/v1/books/cover-upload 响应：相对 ref（随后随建书/改书写 novels.cover_url）。 */
export type CoverUploadResponse = { cover_url: string };

/** PG `integer`（int4）上界——`novel_groups.read_count` / `favorite_count` 的物理上限 */
const INT4_MAX = 2_147_483_647;

/**
 * 书籍创建（ADM-BOK-S003，A6 §16.1）：**同一事务**建一行 `novel_groups`（书组身份 + 编码 +
 * 书组级字段）与该书组的第一个 per-locale `novels` 行（`group_id` 指向新书组、
 * `source_novel_id = NULL`）。
 *
 * 文案字段（title / author_display / description / cover_url）落 novels 行（per-locale 模型，
 * 不再有独立 novel_languages 表）；书组级字段（`cn_title` / `original_*` / `source` / 展示基数）
 * 落 `novel_groups`（dd#970）。**不写 `root_novel_id`** —— root 是译制的前提而非书组的前提
 * （dd#923 语义 6），由 §17 建首个工厂译制语言版时落定。
 */
export const CreateNovelRequestSchema = z
  .object({
    slug: SlugSchema,
    locale: LocaleSchema,
    title: z.string().min(1).max(255),
    description: z.string().min(1),
    cover_url: CoverUrlSchema,
    author_display: z.string().max(255).nullable().optional(),
    // ADM-BOK-S024 改写版本标签（A6 §16.1）：可省略（= 原版 NULL）；传了就 trim 后 1..32。
    // 工厂译制集合成员不得提交 —— 成员判定要读 provider kind，在 service 校验
    edition_label: EditionLabelSchema.optional(),
    /**
     * 中文运营识别名（A6 §16.1 必填，写 `novel_groups.cn_title`，dd#970 新增）——
     * 让运营一眼认出是哪部书，**与书组下有没有中文语言版无关**；与 `original_title`
     * （原作书名，语言由 `original_locale` 指明）是两件事，原作非中文时二者不重合。
     */
    cn_title: z.string().min(1).max(255),
    original_title: z.string().min(1).max(255),
    original_author: z.string().max(255).nullable().optional(),
    original_locale: LocaleSchema,
    source: NovelSourceSchema,
    // ADM-BOK-S004 外部采集扩展信息（仅 IMPORTED root 行）
    source_provider: SourceProviderSchema.nullable().optional(),
    source_external_id: z.string().max(255).nullable().optional(),
    age_rating: AgeRatingSchema.default('ALL_AGES'),
    // ADM-BOK-S016 评分：可选；省略则 DB volatile 随机 default 生成 4.6-4.9（运营默认值），传值则覆盖
    rating: z.number().min(0).max(5).optional(),
    // ADM-BOK-S017 数据基数（阅读 / 收藏展示基数）：书组唯一一份（落 `novel_groups`，dd#970）；省略 → DB default 0。
    // 客户端展示值 = 本基数 + 书组真实聚合（dev-docs `03-数据字典/20-内容.md` §1.1），基数本身不计入运营统计。
    // 上界钉 int4 max：`novel_groups.read_count` 是 PG integer，超界会在 INSERT/UPDATE 时被 DB 拒 → 500；
    // 入口判掉才能返 VALIDATION_FAILED（`03-数据字典/20-内容.md` §1 计数沿用 integer 口径）。
    read_count: z.number().int().min(0).max(INT4_MAX).optional(),
    favorite_count: z.number().int().min(0).max(INT4_MAX).optional(),
    // ADM-BOK-S017 只读派生字段：real_* 由服务端 cron / 写路径维护、display_* 由服务端计算下发。
    // 显式声明为 never 而非依赖 Zod 剥离未知键——契约要求「传入即 VALIDATION_FAILED」，
    // 静默剥离会让调用方以为写成功了（实际是空更新 + 200）。
    real_read_count: z.never().optional(),
    real_favorite_count: z.never().optional(),
    display_read_count: z.never().optional(),
    display_favorite_count: z.never().optional(),
    // ADM-OPS-S009 决策 B：书架推荐语多语言池（选填；书架推荐不校验，A2 §8.5）
    recommendation_text_i18n: RecommendationTextI18nSchema,
    category_id: z.string().uuid(),
    tags: z.array(z.string().uuid()).default([]),
    free_chapter_count: z.number().int().min(0).max(1000).default(5),
    // A6 §16.1：可选；**传了就下限 >= 1**（与 §16.2 改书同一条判据），缺省才按当前 App
    // apps.default_chapter_price_per_kw 填充。不声明会被 z.object 静默剥离 —— 传 '0' 不报
    // VALIDATION_FAILED、传合法值也不生效，随后建的短章按 DB 默认单价派生出与预期不符的价。
    cost_default_per_kw: z
      .string()
      .regex(/^[1-9][0-9]*$/)
      .optional(),
    // §1.5 / A6 §16：系统分配，body 携带即 VALIDATION_FAILED（与 real_* 同款 never）
    book_code: z.never().optional(),
    book_group_code: z.never().optional(),
    // dd#970：书组身份与译制基准行都由服务端定，body 携带即拒（A6 §16.3）
    group_id: z.never().optional(),
    root_novel_id: z.never().optional(),
    // ADM-BOK-S019：建书可选篇幅；价格档由服务端按对照表初始化，body 携带即拒
    length_class: NovelLengthClassSchema.optional(),
    price_level: z.never().optional(),
    // ADM-BOK-S020：新建行买断恒未启用，body 携带即拒（A6 §16.1）——
    // 携带 length_class 只初始化 price_level，不置本列
    is_novel_purchase_enabled: z.never().optional(),
    limited_free_enabled: z.boolean().optional(),
    // ADM-BOK-S025：逐语言版本推荐标记；只接受 JSON 布尔，缺省不改（创建时 false）。
    is_recommended: z.boolean().optional(),
  })
  .refine(hasValidSourceProviderDependency, {
    path: ['source_provider'],
    message:
      'source_provider must be submitted with source_external_id; a non-null external id requires a non-null provider',
  });
export type CreateNovelRequest = z.infer<typeof CreateNovelRequestSchema>;

/**
 * UpdateNovel（A6 §16.2）：编辑某一个 per-locale novel 行的可变字段。
 * `:id` = 任一语言版本行，**不再有「必须是 root」这回事**（dd#970）。
 *
 * slug / locale 创建后不可改（per-locale identity）；`free_chapter_count` 创建后可调（S005），
 * 与 `chapters.is_free` 重算同事务。source_novel_id 不在此处改（reparent 受 DB trigger 约束）。
 *
 * **本端点只写 per-locale 行级字段**：书组级字段（`cn_title` / `original_*` / `source` /
 * `read_count` / `favorite_count`）已随 dd#970 迁至 `novel_groups`，改走 §16.4
 * `PATCH /book-groups/:id`；本 schema 把它们逐个声明为 `never`，提交即 VALIDATION_FAILED
 * （不能靠 `.strict()` 兜底给出泛化报错 —— 契约要求点名这批字段「改走 §16.4」）。
 */
export const UpdateNovelRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().min(1).optional(),
    cover_url: CoverUrlSchema.optional(),
    author_display: z.string().max(255).nullable().optional(),
    // ADM-BOK-S024 改写版本标签（A6 §16.2）：null = 清除（回到原版）；非 null 则 trim 后 1..32。
    // 合并本次提交后属工厂译制集合成员的行提交本字段 → VALIDATION_FAILED（service 校验）
    edition_label: EditionLabelSchema.nullable().optional(),
    // dd#970：书组级字段本端点不接受，改走 §16.4 PATCH /book-groups/:id（提交即 VALIDATION_FAILED）
    cn_title: z.never().optional(),
    original_title: z.never().optional(),
    original_author: z.never().optional(),
    original_locale: z.never().optional(),
    // 权属 source 已迁 `novel_groups`（dd#923 决议一 + dd#970），本端点不接受；
    // 保留的是 per-locale 行级的 source_provider / source_external_id（dd#923 决议二）
    source: z.never().optional(),
    source_provider: SourceProviderSchema.nullable().optional(),
    source_external_id: z.string().max(255).nullable().optional(),
    age_rating: AgeRatingSchema.optional(),
    // ADM-BOK-S016 编辑页手改评分（一位小数 0-5）
    rating: z.number().min(0).max(5).optional(),
    // ADM-BOK-S017 数据基数已随 dd#970 迁至 `novel_groups`（书组唯一一份），本端点不接受 ——
    // 改走 §16.4。原口径（已废弃）：写书组 root 行的基数列、非 root 行传入即拒。
    read_count: z.never().optional(),
    favorite_count: z.never().optional(),
    // ADM-BOK-S017 只读派生字段：real_* 由服务端 cron / 写路径维护、display_* 由服务端计算下发。
    // 显式声明为 never 而非依赖 Zod 剥离未知键——契约要求「传入即 VALIDATION_FAILED」，
    // 静默剥离会让调用方以为写成功了（实际是空更新 + 200）。
    real_read_count: z.never().optional(),
    real_favorite_count: z.never().optional(),
    display_read_count: z.never().optional(),
    display_favorite_count: z.never().optional(),
    // 千字默认单价（novels.cost_default_per_kw）：正整数 COIN 串（bigint），**下限 >= 1**。
    // 单价 0 不是「整本免费书」的表达方式——整书免费走 limited_free_enabled 或 free_chapter_count
    // 覆盖全书（20-内容 §1.6），那两种情况下单价照常、每章照常持有派生价、只是不收取。
    cost_default_per_kw: z
      .string()
      .regex(/^[1-9][0-9]*$/)
      .optional(),
    // ADM-OPS-S009 决策 B：编辑页录入/改书架推荐语多语言池（省略=不动；传 {} = 全部清空）
    recommendation_text_i18n: RecommendationTextI18nSchema,
    category_id: z.string().uuid().optional(),
    tags: z.array(z.string().uuid()).optional(),
    serial_status: SerialStatusSchema.optional(),
    book_code: z.never().optional(),
    book_group_code: z.never().optional(),
    // dd#970：书组身份恒不可改（C 端 trigger `novels_group_immutable` 兜底），入口先拒
    group_id: z.never().optional(),
    // ADM-BOK-S019：null = 取消标注（同事务清 price_level + 关买断，见 service 级联）
    length_class: NovelLengthClassSchema.nullable().optional(),
    price_level: NovelPriceLevelSchema.optional(),
    // ADM-BOK-S020：置 true 需合并本次提交后的 price_level 非空（service 校验，A6 §16.2）
    is_novel_purchase_enabled: z.boolean().optional(),
    // ADM-BOK-S018
    limited_free_enabled: z.boolean().optional(),
    // ADM-BOK-S025：逐语言版本推荐标记；只接受 JSON 布尔，缺省不改（创建时 false）。
    is_recommended: z.boolean().optional(),
    // ADM-BOK-S005：创建后可调；越界 → VALIDATION_FAILED。service 同事务重算 chapters.is_free。
    free_chapter_count: z.number().int().min(0).max(1000).optional(),
    /**
     * 乐观并发前置条件（#355）：调用方载入该行时拿到的 `updated_at`，逐字回传。
     *
     * **不是可写字段** —— service 拿它与行锁内读到的当前 `updated_at` 比对，不等则整笔拒绝
     * （`RESOURCE_CONFLICT`），不落任何写。之所以必要：本端点的调用方（编辑页）把 `title` /
     * `tags` / `recommendation_text_i18n` 等**全量快照**回传，两人先后保存时后者会用自己载入时
     * 的旧值覆盖前者刚写的新值 —— 行锁只让两笔写串行，挡不住这种跨请求的 read-modify-write。
     *
     * 省略 = 不做前置检查（保留既有行为）。脚本 / 一次性修数这类「我就是要覆盖」的调用方
     * 无需构造它；交互式编辑面必须传，判据见 service 侧 invariant 注释。
     */
    expected_updated_at: z.string().datetime().optional(),
  })
  .strict()
  .refine(
    // `expected_updated_at` 是前置条件不是可写字段：只带它 = 一次空更新，须与完全不传字段同样被拒。
    (value) => Object.keys(value).filter((key) => key !== 'expected_updated_at').length > 0,
    { message: 'At least one mutable field is required' },
  )
  .refine(hasValidSourceProviderDependency, {
    path: ['source_provider'],
    message:
      'source_provider must be submitted with source_external_id; a non-null external id requires a non-null provider',
  });
export type UpdateNovelRequest = z.infer<typeof UpdateNovelRequestSchema>;

/**
 * UpdateNovelGroup（A6 §16.4，`PATCH /api/v1/book-groups/:id`）—— 改书组级字段。
 * `:id` = `novel_groups.id`。UPDATE 语义（缺省字段不改）。
 *
 * dd#970 前这些字段散落在「书组根行」上、经 `PATCH /books/<root id>` 修改；书组升格后它们有了
 * 自己的载体，也就有了自己的端点 —— 这同时解掉「root 可空后拿哪个 id 改书组字段」的问题。
 *
 * **不接受的字段**：`id` / `root_novel_id` / `book_group_code` / `next_sequence` /
 * `group_default_visibility` / 三个 `*_score` —— 分别是身份、系统分配、由 §12.1 专用端点写、
 * 服务端内部；逐个声明为 `never` 而非靠 `.strict()`，因为契约要求点名这批字段。
 */
export const UpdateNovelGroupRequestSchema = z
  .object({
    /** 中文运营识别名（S003）；不设 per-locale 覆盖层（判据见字典 §13.2）*/
    cn_title: z.string().min(1).max(255).optional(),
    original_title: z.string().min(1).max(255).optional(),
    /** 可置 null（GDPR 匿名化路径）*/
    original_author: z.string().max(255).nullable().optional(),
    original_locale: LocaleSchema.optional(),
    /**
     * 权属（书组唯一一份）。**书组内任一行有 active chapters 后即锁** → BOOK_SOURCE_IDENTITY_LOCKED
     * （dd#923；锁的判据由「该行」上提到「该组」，因为本列已是书组级）—— 由 service 层校验。
     */
    source: NovelSourceSchema.optional(),
    /** ADM-BOK-S017 展示基数（≥ 0，上界钉 int4 max 同 §16.1）；允许下调 */
    read_count: z.number().int().min(0).max(INT4_MAX).optional(),
    favorite_count: z.number().int().min(0).max(INT4_MAX).optional(),
    // 只读派生 / 服务端内部 / 身份与系统分配：传入即 VALIDATION_FAILED
    real_read_count: z.never().optional(),
    real_favorite_count: z.never().optional(),
    display_read_count: z.never().optional(),
    display_favorite_count: z.never().optional(),
    id: z.never().optional(),
    root_novel_id: z.never().optional(),
    book_group_code: z.never().optional(),
    next_sequence: z.never().optional(),
    group_default_visibility: z.never().optional(),
    popularity_score: z.never().optional(),
    most_read_score: z.never().optional(),
    most_loved_score: z.never().optional(),
    /**
     * 乐观并发前置条件：取 §15.2 顶层 `updated_at`（= `novel_groups.updated_at`）逐字回传。
     * 语义与精度口径同 §16.2 同名字段；省略 = 不做该检查。
     */
    expected_updated_at: z.string().datetime().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).filter((key) => key !== 'expected_updated_at').length > 0, {
    message: 'At least one mutable field is required',
  });
export type UpdateNovelGroupRequest = z.infer<typeof UpdateNovelGroupRequestSchema>;

/**
 * ADM-BOK-S006 多语言版本（A6 §17，`POST /api/v1/book-groups/:id/languages`）：在已有书组下
 * 新增一个语言版本 = 新建一行 novels，`group_id = :id`（`:id` = `novel_groups.id`，dd#970：
 * **不再是「书组根 novel_id」**）。同书组同 locale 唯一活跃，由 DB partial unique 兜底。
 * category / tags / age_rating 缺省由 service 以书组代表行当前值初始化，显式传入则保存目标语言值；
 * `original_*` 与 `source` 在 `novel_groups` 上一份（dd#970 迁入后镜像机制整个消失），不是本端点入参。
 */
export const CreateNovelLanguageRequestSchema = z.object({
  locale: LocaleSchema,
  title: z.string().min(1).max(255),
  description: z.string().min(1),
  cover_url: CoverUrlSchema,
  author_display: z.string().max(255).nullable().optional(),
  // #649 / #666：注册表与译制任务原子写尚未交付，拒绝来源输入，避免被 strip 后误建手工版本。
  source_provider: z.never().optional(),
  source_external_id: z.never().optional(),
  translation_source_novel_id: z.never().optional(),
  // ADM-BOK-S024（A6 §17）：可省略（= 原版 NULL，不继承同组其他行）；传了就 trim 后 1..32。
  // 显式声明 —— 本 schema 非 strict，不声明会被静默 strip；「工厂译制平台 + 携带本字段 →
  // VALIDATION_FAILED」要读 provider kind，由 service 显式拒
  edition_label: EditionLabelSchema.optional(),
  free_chapter_count: z.number().int().min(0).max(1000).default(5),
  category_id: z.string().uuid().optional(),
  tags: z.array(z.string().uuid()).optional(),
  age_rating: AgeRatingSchema.optional(),
  book_code: z.never().optional(),
  // ADM-BOK-S020：新语言版本买断恒 false、不以 root 值初始化，body 携带即拒（A6 §17）——
  // 本 schema 非 strict，不显式声明会被静默 strip、拿不到契约要求的 VALIDATION_FAILED
  is_novel_purchase_enabled: z.never().optional(),
  // 新语言版本不继承推荐标记；携带入参须显式拒绝，不能静默剥离。
  is_recommended: z.never().optional(),
});
export type CreateNovelLanguageRequest = z.infer<typeof CreateNovelLanguageRequestSchema>;

/**
 * §17 创建语言版本响应：保留既有 v1 NovelResponse 全量字段，并新增 novel_id 供新消费方使用。
 * 删除/重命名 v1 字段属于 breaking change，不能用更小摘要直接替换。
 */
export type NovelLanguageSummary = NovelResponse & { novel_id: string };

/**
 * 书籍文案翻译（ADM-BOK 编辑页「从英文翻译」）— 翻译预览：复用 DeepL 通道（源语言自动检测），
 * 把原版文案（书名/作者/简介）翻成目标 locale，返回译文供运营填表后随保存落库（端点本身不写库、不审计）。
 */
export const TranslateNovelFieldsRequestSchema = z.object({
  texts: z.array(z.string().min(1).max(5000)).min(1).max(8),
  target_locale: LocaleSchema,
});
export type TranslateNovelFieldsRequest = z.infer<typeof TranslateNovelFieldsRequestSchema>;
export type TranslateNovelFieldsResponse = {
  translations: string[];
  detected_source_lang: string | null;
};

/**
 * 层② 单书覆盖写（A6 §12.2 `POST /books/:id/visibility`，ADM-BOK-S011 / S013）。
 *
 * `visibility: null` 是**有意义的入参**、不是「不传」：清除本层覆盖、回落继承层①，与「配
 * `PUBLIC`」语义不同 —— 前者跟随书组默认层变动，后者钉死为全体可见。故用 `.nullable()` 且必填。
 */
export const SetNovelVisibilityRequestSchema = z.object({
  visibility: NovelVisibilitySchema.nullable(),
  // A6 §12 共享判据：DELISTED 前置要的是「该行 merge 后 status_reason_i18n.en 非空」，
  // 来源可以是**入参新填 或 既有值** —— 故本层只校字段形状，不在这里强制入参必带 en。
  // 合并后的有效性由事务内 service 判（缺失 → BOOK_DELIST_REASON_REQUIRED，不是 VALIDATION_FAILED）。
  status_reason_i18n: z.record(z.string(), z.string()).optional(),
});

export type SetNovelVisibilityRequest = z.infer<typeof SetNovelVisibilityRequestSchema>;

/**
 * 层① 书组默认层写（A6 §12.1 `POST /book-groups/:id/group-visibility`，ADM-BOK-S021）。
 *
 * 与层② 的差别：**不可为 `null`** —— 层① 恒有值，是三层里唯一 NOT NULL 的一层。
 */
export const SetGroupDefaultVisibilityRequestSchema = z.object({
  visibility: NovelVisibilitySchema,
  // 同层②：只校形状。§12.1 要求 merge 后**逐行**校验各 active per-locale 行，缺失行经
  // BOOK_DELIST_REASON_REQUIRED 的 details.missing_reason_novel_ids 返回 —— 在这里拦死会让
  // 「各行已有合法原因、只改档」的合法请求拿不到 422 之外的任何东西，也到不了缺失行明细。
  status_reason_i18n: z.record(z.string(), z.string()).optional(),
});

export type SetGroupDefaultVisibilityRequest = z.infer<
  typeof SetGroupDefaultVisibilityRequestSchema
>;

/**
 * 层③ App 级覆盖写（A6 §13.1，ADM-BOK-S012）。
 *
 * **四档全开**（dd#956 起）：上层为非 `PUBLIC` 档时本层配 `PUBLIC` 是有业务意义的**放开**，
 * 与「无覆盖行」（继承上层）语义不同，**不可用删行代替**；恢复继承上层走 DELETE（软删本行）。
 */
export const SetAppOverrideRequestSchema = z.object({
  app_id: z.string().uuid(),
  visibility: NovelVisibilitySchema,
  // App 级覆盖时填写的原因——**写入全量 novels.status_reason_i18n**（单点维护，novel_app_overrides
  // 无 reason 列）。可选：缺省时沿用既有全局原因（DELISTED 仍要求全局 reason.en 非空）。
  status_reason_i18n: z.record(z.string(), z.string()).optional(),
});
export type SetAppOverrideRequest = z.infer<typeof SetAppOverrideRequestSchema>;

export const RemoveAppOverrideRequestSchema = z.object({
  app_id: z.string().uuid(),
});
export type RemoveAppOverrideRequest = z.infer<typeof RemoveAppOverrideRequestSchema>;

/**
 * ADM-BOK-S007 / S009 章节管理 — per-locale 建模下 locale 由 novel_id 隐含（不再有 chapters.locale）；
 * 章节挂在某一个 per-locale novel_id 下（URL path /books/:novelId/chapters）。
 */
export const CreateChapterRequestSchema = z
  .object({
    chapter_index: z.number().int().positive(),
    title: z.string().min(1).max(255),
    word_count: z.number().int().nonnegative(),
    // cost / is_free 不在本 shape：createChapter 在建章事务内按 20-内容 §3.0 从 word_count +
    // chapter_index + 该 novel 的 cost_default_per_kw / free_chapter_count 派生，调用方不得传。
    content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    content_r2_key: z.string().min(1).max(255),
    // #454 试读快照：建章必随正文派生（prepareLanguageAwarePreview(novel.locale).build(content,
    // LOCKED_CHAPTER_PREVIEW_UNITS)），必填——章节一创建即有正文，不存在「未改」语义。
    locked_preview_text: z.string(),
    locked_preview_truncated: z.boolean(),
  })
  .strict();
export type CreateChapterRequest = z.infer<typeof CreateChapterRequestSchema>;

/**
 * updateChapter **内部**输入 shape（service-only，非公开路由 schema）。含 content 指针 / word_count 等正文派生量，
 * 仅供 setChapterContent / createChapterWithContent 经受控代理上传后派生回填；公开 PATCH 走
 * PatchChapterMetadataRequestSchema（下方），不得设这些字段。
 */
export const UpdateChapterRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    word_count: z.number().int().nonnegative().optional(),
    // cost / is_free 不在本 shape：未发布章由 updateChapter 在事务内按 §3.0 现算后直写线上列；
    // 已发布章两列无草稿列、不预存，随 §9.7 提升事务现算（chapter-publish-admin-service）。
    content_sha256: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
    content_r2_key: z.string().min(1).max(255).optional(),
    // #454 试读快照：仅正文改动时随 content_r2_key/content_sha256 一并透传（可选——元数据-only SAVE
    // 不带正文派生量，缺省时下游 draft/live 列保持不变，见 chapter-management-admin-service）。
    locked_preview_text: z.string().optional(),
    locked_preview_truncated: z.boolean().optional(),
    expected_content_version: z.number().int().nonnegative().optional(),
    expected_base_snapshot_version: z.number().int().nonnegative().optional(),
    expected_draft_content_version: z.number().int().nonnegative().optional(),
  })
  .strict();
export type UpdateChapterRequest = z.infer<typeof UpdateChapterRequestSchema>;

/**
 * 章节元数据编辑（公开 PATCH /books/chapters/:id）——仅运营可手编的元数据字段。
 * **刻意不含** content_r2_key / content_sha256 / word_count（正文派生量）：正文指针只能经 setChapterContent /
 * createChapterWithContent 受控代理上传后服务端派生，公开 PATCH 不得重指——否则可绕过「上传 + sha 校验」，把
 * C 端章节正文指针指向任意 / 不存在对象（adversarial r1#1 P0：内容服务旁路）。
 */
export const PatchChapterMetadataRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    // A6 §9.4 刻意不含 cost / is_free（同 §9.3）：不存在手动价，也不存在单章手工标免费。
    // 要改某章价格改其正文长度或该 novel cost_default_per_kw（§16.2）；要改免费边界改 free_chapter_count。
    cost: z.never().optional(),
    is_free: z.never().optional(),
    expected_base_snapshot_version: z.number().int().nonnegative().optional(),
    expected_draft_content_version: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine((input) => input.title !== undefined, 'At least one chapter metadata field is required');
export type PatchChapterMetadataRequest = z.infer<typeof PatchChapterMetadataRequestSchema>;

// ─── 章节正文读/写（ADM-BOK 运营编辑正文；admin-first 实现，dev-docs 同步收录中）────────────
// 正文 blob 走 R2 `novels/{novelId}/chapters/{chapterId}.txt`（P1-storage-layout）；admin 代理上传
// （前端发纯文本 → server 计算 sha256/word_count；未发布章直接 CAS，已发布章只写 pending draft）。
// 非 presigned（纯文本小 blob，免 R2 S3 凭证）。
export const SetChapterContentRequestSchema = z
  .object({
    content: z.string().min(1).max(2_000_000),
    // 未发布章 = chapters.content_version；已发布章 = chapter_drafts.draft_content_version。
    expected_content_version: z.number().int().nonnegative(),
    // 已发布章 SAVE 必填；运行态按 published_at 分流后校验。
    expected_base_snapshot_version: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SetChapterContentRequest = z.infer<typeof SetChapterContentRequestSchema>;

/**
 * 新建章节（运营手动撰写）— 代理创建：前端发元数据 + 正文纯文本，server 生成 chapterId → 计算
 * content_sha256/word_count → PUT R2 `novels/{novelId}/chapters/{chapterId}.txt` → 受控 service INSERT。
 * 解 CreateChapterRequest 的鸡生蛋（content_r2_key 派生自尚不存在的 chapterId + 前端无 R2 直传通道）。
 * admin-first 实现（写既有 C 端 chapters 列、不改 schema），dev-docs 同步收录中。
 */
export const CreateChapterWithContentRequestSchema = z
  .object({
    chapter_index: z.number().int().positive(),
    title: z.string().min(1).max(255),
    // A6 §9.3 刻意不含 cost / is_free：两列唯一权威恒为 20-内容 §3.0 派生式，误传 VALIDATION_FAILED。
    // 显式 never 而非靠 .strict() 的 unrecognized_keys——errors[].field 要落在具体字段上。
    cost: z.never().optional(),
    is_free: z.never().optional(),
    content: z.string().min(1).max(2_000_000),
  })
  .strict();
export type CreateChapterWithContentRequest = z.infer<typeof CreateChapterWithContentRequestSchema>;

export const RepublishChapterRequestSchema = z
  .object({
    expected_base_snapshot_version: z.number().int().nonnegative(),
    expected_draft_content_version: z.number().int().nonnegative(),
  })
  .strict();
export type RepublishChapterRequest = z.infer<typeof RepublishChapterRequestSchema>;

export const RebaseChapterDraftRequestSchema = z
  .object({
    expected_draft_content_version: z.number().int().nonnegative(),
  })
  .strict();
export type RebaseChapterDraftRequest = z.infer<typeof RebaseChapterDraftRequestSchema>;

export const SetPublishScheduleRequestSchema = z
  .object({
    enabled: z.boolean(),
    scheduled_publish_at: WallClockTimeSchema.nullable().optional(),
  })
  .strict();
export type SetPublishScheduleRequest = z.infer<typeof SetPublishScheduleRequestSchema>;

// 读正文（编辑器回填）：已发布章优先覆盖 pending 草稿。
export type GetChapterContentResponse = {
  content: string;
  content_version: number;
  content_sha256: string;
  title: string;
  cost: string;
  is_free: boolean;
  word_count: number;
  has_pending_draft: boolean;
  base_snapshot_version: number | null;
  draft_updated_at: string | null;
  draft_last_edited_by_name: string | null;
};

/**
 * 书籍列表筛选查询参数（ADM-BOK-S006 列表筛选 — 服务端下推，替换前端单页内存过滤的 P1 回归）。
 *
 * 全部可选；与 cursor/limit（parsePageParams）正交，不进本 schema。列表以书组行（`novel_groups`）作 keyset：
 * - source 命中**书组行**（`novel_groups.source`，dd#970 迁入；此前按 root 行过滤 —— root 可空后
 *   该谓词对无 root 的书组恒不命中）；
 * - category / tag / lang / status / serial_status 必须由同一个 active member 同时命中；
 * - q 可命中任一 active member 文案，合法 UUID 另精确匹配 group_id。
 *
 * **多值参数（source / category / tag / lang / serial_status）以重复 query 参数传递**（如 `?source=ORIGINAL&source=LICENSED`），
 * 路由层用 `c.req.queries('<name>')` 取 string[]；单值 q / visibility 用 `c.req.query('<name>')`。
 * 任一非法（未知枚举 / 非 uuid / 超长）→ VALIDATION_FAILED(422)。
 *
 * 前端契约（精确参数名）：q / visibility / source / category / tag / lang / serial_status。
 */
const BookListSourceSchema = z.enum(['ORIGINAL', 'LICENSED', 'IMPORTED']);
const BookListSerialStatusSchema = z.enum(['ONGOING', 'COMPLETED', 'HIATUS']);
export const BookListQuerySchema = z.object({
  /** 投放选书专用：只返回目标产品关联 App 均未下架的语言行。 */
  landing_product_id: z.uuid().optional(),
  /**
   * 关键词：书组成员的 title / author_display 与书组行的 original_title / original_author / cn_title
   * 模糊匹配；另精确匹配 active member 的 source_external_id（不透明字符串）；q 为合法 uuid 时另精确匹配 `novels.group_id`（dd#970 前匹配 root.id）
   */
  q: z.string().trim().min(1).max(255).optional(),
  /**
   * per-locale 受众档；与其它 member 筛选由同一 active 行命中；单值。
   *
   * **比较值 = `COALESCE(novels.visibility, novel_groups.group_default_visibility)`**（层② 覆盖
   * 层①，**不含层③**）—— 本端点是跨 App 的书组列表、无 App scope 入参，层③ 只能按 §10 逐 App 查。
   * 与列表展示口径同源（A6 §15.1），否则会出现「筛出来了但这列是空的」。
   */
  visibility: NovelVisibilitySchema.optional(),
  /** 权属（书组行 `novel_groups.source`，dd#970）；多值 OR */
  source: z.array(BookListSourceSchema).optional(),
  /** per-locale 主分类；与其它 member 筛选由同一 active 行命中；多值 OR */
  category: z.array(z.string().uuid()).optional(),
  /** per-locale 标签数组重叠；与其它 member 筛选由同一 active 行命中；多值 OR */
  tag: z.array(z.string().uuid()).optional(),
  /** per-locale 语言；与其它 member 筛选由同一 active 行命中；多值 OR */
  lang: z.array(z.string().min(1).max(16)).optional(),
  /** per-locale 连载状态；与其它 member 筛选由同一 active 行命中；多值 OR */
  serial_status: z.array(BookListSerialStatusSchema).optional(),
  /** A6 §15.1：query 仅收字面 true/false，输出布尔供服务和界面共用。 */
  is_recommended: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});
export type BookListQuery = z.infer<typeof BookListQuerySchema>;

/**
 * NovelResponse — 单个 per-locale novel 行（A6 §15.3；详情页里书组的一条语言版本）。
 * per-locale 文案字段（locale / title / description / cover_url / author_display）直接在本行。
 * `group_id` 直接取自 `novels.group_id`（dd#970 前为 `group_root_id = COALESCE(source_novel_id, id)`）；
 * 返回 translation 行时不得替换为 root 行。
 *
 * 书组级字段（`cn_title` / `original_*` / `source` / 展示基数）由服务端按 `group_id`
 * join `novel_groups` 回填 —— 本类型是 per-locale 行视图，这几项在全组相同。
 */
export type NovelResponse = {
  id: string;
  /** 当前 per-locale 行的书级编码（canonical 10 位）；界面标识，须同列展示 locale */
  book_code: string;
  /** 所属书组编码（canonical 7 位），按本行 group_id 查 novel_groups，禁止截 book_code 前 7 位 */
  book_group_code: string;
  slug: string;
  locale: string;
  title: string;
  description: string;
  cover_url: string;
  author_display: string | null;
  /**
   * ADM-BOK-S024 **有效**改写版本标签（A6 §15.3）：非工厂译制集合成员取本行 `edition_label`；
   * 成员取书组译制基准行（`root_novel_id`）的值，基准为空时 `null`。`null` = 原版
   */
  edition_label: string | null;
  /** 书组级：中文运营识别名（`novel_groups.cn_title`，dd#970）*/
  cn_title: string;
  /** 书组级：原作书名 */
  original_title: string;
  /** 书组级：原作者 */
  original_author: string | null;
  /** 书组级：原作语言 */
  original_locale: string;
  /** 书组级：内容权属（`novel_groups.source`，dd#970 迁入）*/
  source: 'ORIGINAL' | 'LICENSED' | 'IMPORTED';
  /** per-locale 行级：该行内容的提供方（dd#923 决议二）*/
  source_provider: 'TENTOO' | null;
  source_external_id: string | null;
  /** **译制来源指针**：译制行指向本组 root_novel_id；非译制行为 null。**不表达分组** */
  source_novel_id: string | null;
  /** 所属书组 id（`novels.group_id`）。界面不展示，仅供前端组装后续请求 */
  group_id: string;
  serial_status: 'ONGOING' | 'COMPLETED' | 'HIATUS';
  age_rating: 'ALL_AGES' | 'TEEN_13' | 'MATURE_16' | 'ADULT_18';
  /** ADM-BOK-S016 运营展示评分（numeric(2,1) → number；非真实用户评分）；创建随机 4.6-4.9，可编辑 */
  rating: number;
  /**
   * ADM-BOK-S017 阅读展示基数（**书组级**，`novel_groups.read_count`，按 group_id join 回填）。
   * dd#970 前仅书组 root 行返回、译版省略；书组唯一一份后**每行都返回同一个值**，不再有
   * 「哪一行有」的问题。
   *
   * **例外**：§15.2 书组详情的 `languages[]` 内各项一律省略这两个 key —— 该端点的书组基数
   * 只在顶层三元组（`NovelGroupDetail.read_count` / `real_*` / `display_*`），
   * 逐项重复会让消费方以为语言版本有独立基数。§17 建语言版本响应同此省略口径。
   */
  read_count?: number;
  /** ADM-BOK-S017 收藏展示基数；带出口径同 read_count */
  favorite_count?: number;
  /** ADM-OPS-S009 决策 B：书架推荐语多语言池（locale → 文案）；未配=null。选填，书架推荐不校验 */
  recommendation_text_i18n: Record<string, string> | null;
  category_id: string;
  tags: string[];
  total_chapters: number;
  free_chapter_count: number;
  total_words: number;
  /** ADM-BOK-S019 篇幅；null = 未标注 */
  length_class: 'SHORT' | 'MEDIUM' | 'MEDIUM_LONG' | 'LONG' | 'EPIC' | null;
  /** ADM-BOK-S019 价格档**列原值**；null = 未标注。**不按 is_novel_purchase_enabled 投影**——
   *  与 C 端详情的投影语义（`02-API契约/20-内容库.md` §4）不同，运营要看到真实标注 */
  price_level: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | null;
  /** ADM-BOK-S020 买断启用（per-locale，只经 §16.2 置位）*/
  is_novel_purchase_enabled: boolean;
  /** ADM-BOK-S018 书籍级免费 */
  limited_free_enabled: boolean;
  /** ADM-BOK-S025：当前语言版本原值，不跨 sibling 继承。 */
  is_recommended: boolean;
  cost_default_per_kw: string;
  published_at: string | null;
  /**
   * 层② 单书覆盖层**原值回显**（A6 §15.3）——`null` = 未覆盖、继承层①。
   *
   * **不是 effective 值**：详情是编辑面，「这一行有没有自己的覆盖」正是 §12.2 单书覆盖入口要读的
   * 信息，故此处不合成（列表面才按 §15.1 合成，见 `NovelGroupSummary.languages[]`）。
   */
  visibility: NovelVisibility | null;
  /** 层① 书组默认层当前值（`novel_groups.group_default_visibility`）；属书组、同组各行同值 */
  group_default_visibility: NovelVisibility;
  status_reason_i18n: Record<string, string> | null;
  created_at: string;
  updated_at: string;
};

/**
 * NovelGroupSummary — 列表一行（一个书组聚合，A6 §15.1）。
 * 列表按 `novels.group_id` 聚合，避免同一本书的多语言行重复出现。
 * 书组标题展示用 `cn_title`（中文运营识别名，dd#970）；languages 给出已有语言版本概览。
 */
export type NovelGroupSummary = {
  /** 书组 id（`novel_groups.id`）；列表详情跳转与全部书组级操作使用本字段 */
  group_id: string;
  /** 书组可阅读编码（7 位）。C 端未回传时缺省。 */
  book_group_code?: string | null;
  /**
   * 本次列表查询下该行应展示 / 操作的 per-locale novel（选取规则见 A6 §15.1「display_novel_id
   * 的选取」）；null 表示没有可信的单一写目标 —— **前端不得用它替代 `group_id` 做书组级操作**。
   * optional 仅用于 Web-first 滚动兼容旧 Worker。
   */
  display_novel_id?: string | null;
  /**
   * 该书组的**译制基准行** id（`novel_groups.root_novel_id`）；书组不做工厂译制时为 null
   * （dd#923 语义 6）。**不是书组身份、不用于路由**（书组身份见 `group_id`）。
   */
  root_novel_id?: string | null;
  /** 书组级：中文运营识别名（dd#970）；admin 列表书组标题按本字段展示 */
  cn_title: string;
  original_title: string;
  original_author: string | null;
  /** @deprecated v1 滚动发布兼容；新消费方从 languages[] 选择明确 locale。 */
  cover_url?: string;
  original_locale: string;
  source: 'ORIGINAL' | 'LICENSED' | 'IMPORTED';
  /** @deprecated v1 滚动发布兼容；新消费方从 languages[] 读取。 */
  age_rating?: 'ALL_AGES' | 'TEEN_13' | 'MATURE_16' | 'ADULT_18';
  /** @deprecated v1 滚动发布兼容；新消费方从 languages[] 读取。 */
  category_id?: string;
  /** @deprecated v1 滚动发布兼容；新消费方从 languages[] 读取。 */
  tags?: string[];
  /** 该书组所有 active 语言版本概览（按 locale）*/
  languages: Array<{
    novel_id: string;
    /** 该语言行 book_code（10 位）；展示处必须同列带 locale。 */
    book_code?: string | null;
    locale: string;
    title: string;
    /** per-locale 展示作者；optional 仅用于 Web-first 滚动兼容旧 Worker。 */
    author_display?: string | null;
    cover_url: string;
    age_rating: 'ALL_AGES' | 'TEEN_13' | 'MATURE_16' | 'ADULT_18';
    category_id: string;
    tags: string[];
    /** 层② 原值（`null` = 未覆盖）——**展示与筛选一律用 `listDisplayVisibility` 合成**，见下行 */
    visibility: NovelVisibility | null;
    /** 层① 书组默认档；属书组、`languages[]` 逐项回显同一个值（A6 §15.1 归属三分表）*/
    group_default_visibility: NovelVisibility;
    serial_status: 'ONGOING' | 'COMPLETED' | 'HIATUS';
    /** ADM-BOK-S025：每项回显自身推荐标记。 */
    is_recommended: boolean;
    total_chapters: number;
    free_chapter_count?: number;
    cost_default_per_kw?: string;
    total_words?: number;
    description?: string | null;
    published_at?: string | null;
    /** ADM-BOK-S019 篇幅（定性分类）；列表行展示用，未标注为 null。optional 兼容旧 Worker。 */
    length_class?: NovelResponse['length_class'];
    source?: NovelResponse['source'];
    source_provider?: NovelResponse['source_provider'];
    /**
     * ADM-BOK-S024 有效改写版本标签，口径同 §15.3 `NovelResponse.edition_label`（成员跟随译制基准行）。
     * 列表行展示版本 tag 用；optional 兼容旧 Worker。
     */
    edition_label?: NovelResponse['edition_label'];
  }>;
  /** @deprecated v1 滚动发布兼容；旧语义为全部 active 语言版本章节数之和。 */
  total_chapters?: number;
  /** 书组创建时间（`novel_groups.created_at`）*/
  created_at: string;
  /** 全部 active 语言版本 MAX(updated_at)，列表排序用 */
  updated_at: string;
};

/**
 * NovelGroupDetail — 书组详情（A6 §15.2，`GET /api/v1/book-groups/:id`）。
 * `group_id` 标识书组；languages 为按 locale 排序的所有 active 语言版本完整行。
 * **本类型不要求书组有 root 行** —— root 可空（dd#923 语义 6），书组资料全部取自 `novel_groups`。
 */
export type NovelGroupDetail = {
  /** 书组 id（`novel_groups.id`）*/
  group_id: string;
  /**
   * 该书组的译制基准行 id；书组不做工厂译制时为 null。**不是书组身份**，界面不展示。
   */
  root_novel_id: string | null;
  /** 书组可阅读编码；C 端未回传时缺省。 */
  book_group_code?: string | null;
  /** 书组级：中文运营识别名（dd#970）；详情页书组标题按本字段展示 */
  cn_title: string;
  original_title: string;
  original_author: string | null;
  original_locale: string;
  /** 书组级：内容权属（`novel_groups.source`）*/
  source: 'ORIGINAL' | 'LICENSED' | 'IMPORTED';
  /**
   * 书组级：层① 默认可见受众档（`novel_groups.group_default_visibility`，A6 §15.2 列在书组级
   * 字段里）。`languages[]` 各项（`NovelResponse`）也逐项回显同一个值，本字段是书组维度的那一份
   * —— §12.1 书组默认层写入口读的是它。
   */
  group_default_visibility: NovelVisibility;
  /**
   * ADM-BOK-S017 数据基数（`novel_groups` 上的书组唯一一份）——契约 A6 §15.2 把它定义在
   * **书组详情顶层**，`languages[]` 条目一律省略同名 key（含代表行那项）。
   */
  read_count: number;
  favorite_count: number;
  /**
   * ADM-BOK-S017 真实值 + 客户端展示值（A6 §15.2 三元组「基数 + 真实 = 展示」）。
   *
   * `real_*` 是 C 端服务端写的物化列（`novel_groups.real_read_count` / `real_favorite_count`），
   * admin **只读**；`display_*` 由服务端派生下发（= 基数 + 真实），前端**不自算**
   * ——口径单点在服务端（`03-数据字典/20-内容.md` §1.1），前端自算会在合成规则变化时静默漂移。
   */
  real_read_count: number;
  real_favorite_count: number;
  display_read_count: number;
  display_favorite_count: number;
  /** 书组创建 / 更新时刻（`novel_groups`）。`updated_at` 兼作 §16.4 乐观并发令牌来源 */
  created_at: string;
  updated_at: string;
  languages: NovelResponse[];
};

export type ChapterResponse = {
  id: string;
  novel_id: string;
  chapter_index: number;
  title: string;
  word_count: number;
  cost: string;
  is_free: boolean;
  content_version: number;
  snapshot_version: number;
  published_at: string | null;
  has_pending_draft: boolean;
  base_snapshot_version: number | null;
  // 最后修改审计（admin-first 透出既有镜像列；编辑器顶栏「已保存 · 时刻」+ 正文 meta「最后修改 by X」接真）：
  //   updated_at               ← chapters.updated_at（每次写元数据/正文 defaultNow 刷新）
  //   last_edited_by_admin_id  ← chapters.last_edited_by_admin_id（最后一次运营手动编辑者；fanout 自动写入不置此列 → 可能 null）
  //   last_edited_by_name      ← resolveAdminNicknames 解析展示名（nickname 优先回退 email；id 为空 / admin
  //                              物理删除时 null；软删 admin 仍按 audit-target-resolver 同口径展示历史名，不过滤 deleted_at）
  updated_at: string;
  last_edited_by_admin_id: string | null;
  last_edited_by_name: string | null;
};

export type PublishScheduleResponse = {
  enabled: boolean;
  scheduled_publish_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
};

export type PublishQueueStatus = 'PENDING' | 'CLAIMED' | 'PUBLISHED' | 'FAILED' | 'CANCELLED';

export type PublishQueueResponse = {
  status: PublishQueueStatus;
  target_chapter_id: string;
  scheduled_at: string | null;
  attempts: number;
  last_error: string | null;
  enqueued_by: string | null;
  enqueued_at: string | null;
  published_at: string | null;
};

export type PublishChapterResponse =
  | (ChapterResponse & { published_chapter_ids: string[] })
  | PublishQueueResponse;

/**
 * ADM-BOK-S014 书组运营统计（A6 §19）——**按书组聚合**（`novels.group_id = :id`，`:id` 是
 * `novel_groups.id`）。
 *
 * `reader_user_count` 是**全时累计** UV，直接读 `novel_groups.real_read_count` 单调计数器（dd#970
 * 前读书组 root 行的 `novels.real_read_count`）；
 * 契约明令**禁止**在本端点 `COUNT(DISTINCT user_id)` 重算 —— `reading_progress` 无 `deleted_at`
 * 且 GDPR hard-delete 把 `user_id` 改写为 `SENTINEL_USER_ID` 会折叠已注销用户，distinct 随注销回退。
 * ⚠ 与 ADM-DASH-S008 `top_books[].readers`（`range` 窗口内活跃 UV）**不同口径**，不可互相引用。
 */
export type NovelStatsResponse = {
  /** 书组 id（`novel_groups.id`）；dd#970 前本字段装的是书组根 novel_id */
  group_id: string;
  reader_user_count: number;
  /** 有效 unlock_records 去重 user_id；SENTINEL 折叠已注销用户合计计 1（A6 §19 接受近似，非结算） */
  paid_user_count: number;
  paid_chapter_count: number;
};

/**
 * ADM-BOK 书籍详情·上下架 Tab — per-App 可见性状态（admin-first 只读派生，dev-docs 同步收录中）。
 *
 * 顶层给出层① / 层② 两层的**当前值**（层③ 逐 App 列在 `apps[]`）；`apps[]` 行集 = 投放名单
 * （`target.locale ∈ apps.allowed_languages ∪ {'en'}` 的未软删 App，同 C 端「书在哪些 App」判据）
 * **∪** 有 active override 但已不在投放名单的 App（orphan override，供运营清理）。每行的
 * `effective_visibility` 按三层解析求值，并用 `source_layer` 说明它**来自哪一层** —— 否则运营
 * 无法判断当前值从哪来、该改哪一层。changed_by / changed_at 仅 override 行有（override 的
 * createdByAdminId 解析昵称 + updatedAt）；launched_at = 目标行 published_at（无 per-App 源）。
 * 纯只读：不写任何表、不改 schema。
 */
export type BookAppStatusResponse = {
  /** 层① 书组默认层当前值（恒非空）；生效范围 = 全 App × 该书组全部语言版本 */
  group_default_visibility: NovelVisibility;
  /** 层② 目标 per-locale 行的单书覆盖层当前值；`null` = 未覆盖、继承层① */
  novel_visibility: NovelVisibility | null;
  /** 目标行的原因池；per-App 下架原因单点引用它（override 无 reason 列），且 reason 不上提书组 */
  global_reason_i18n: Record<string, string> | null;
  apps: Array<{
    app_id: string;
    /** 三层解析结果（字典 §1.2）*/
    effective_visibility: NovelVisibility;
    /** effective 值来自哪一层：APP = 本行有 active override；NOVEL = 层② 非空；GROUP_DEFAULT = 回落层① */
    source_layer: 'APP' | 'NOVEL' | 'GROUP_DEFAULT';
    /** ⇔ `source_layer === 'APP'` */
    has_override: boolean;
    changed_by: string | null;
    changed_at: string | null;
    launched_at: string | null;
  }>;
};

// ─── ADM-BOK-S015 书籍操作与事件日志 ────────────────────────────

/**
 * 日志 Tab 筛选 query：
 *   - `type`：日志类型。`operation` = 运营操作日志（源 admin_audit_logs，元素 BookOperationLogItem）；
 *     `system` = 系统事件日志（源 novel_system_events，元素 SystemEventLogItem）。缺省 = operation。
 *   - `from` / `to`：时间范围（ISO 8601，绝对时刻）。operation 按 created_at 过滤；system 按 occurred_at 过滤。
 */
export const BookOperationLogQuerySchema = z.object({
  type: z.enum(['operation', 'system']).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type BookOperationLogQuery = z.infer<typeof BookOperationLogQuerySchema>;

/**
 * 单条运营操作日志（一行 admin_audit_logs 投影）。
 * `before` / `after` 为字段变更前后快照（jsonb），前端展开查看完整 diff。
 */
export type BookOperationLogItem = {
  id: string;
  action: string; // 操作类型：create_novel / update_novel / publish_chapter / change_novel_status_* / set_app_override_* 等
  resource: string; // novels / chapters / novel_app_overrides
  resource_id: string | null;
  /** 操作对象展示名（按 resource 只读 JOIN 业务表解析：书名 / App 名 …；解析不到→中文资源标签；未知→null）。与全局操作日志同口径，admin 自有契约新增字段（非 breaking）。 */
  target_name: string | null;
  /** diff 字段值展示名映射 `{ 原始值: 展示名 }`：before/after 里 `*_id`（app_id/novel_id…）裸 UUID → 业务名，前端 diff 友好显示；无→null。与全局操作日志同口径，admin 自有契约新增字段（非 breaking）。 */
  field_refs: Record<string, string> | null;
  actor_id: string;
  actor_nickname: string; // 操作人
  actor_email: string;
  before: unknown;
  after: unknown;
  created_at: string; // ISO 8601
};

/**
 * 单条系统事件日志（一行 novel_system_events 投影，A6 §7.1）。
 * `detail` 为 jsonb 结构化上下文；无则 null。
 */
export type SystemEventLogItem = {
  id: string;
  novel_id: string;
  event_type: string; // scheduled_publish / auto_collect_update / status_change
  trigger_source: string; // cron / collect_task / state_machine
  result: string; // success / failed
  summary: string;
  detail: Record<string, unknown> | null;
  occurred_at: string; // ISO 8601
};
